import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/src/components/ui/table';
import { Button } from '@repo/ui/src/components/ui/button';
import { cn } from '@repo/ui/src/lib/utils';

/** Column meta consumed by DataTable — set `sortField` to make a column's
 *  header a sort toggle. Must match a field the backend's per-route sort
 *  whitelist actually accepts (see adminOrgs.ts / adminAudit.ts). */
export interface DataTableColumnMeta {
  sortField?: string;
  headerClassName?: string;
  cellClassName?: string;
}

interface DataTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  data: T[];
  total: number;
  page: number;
  limit: number;
  sort: string;
  onPageChange: (page: number) => void;
  onSortChange: (sort: string) => void;
  isLoading?: boolean;
  /** Set when the underlying query failed — rendered as a distinct error row
   *  so a backend outage can't be mistaken for a genuinely empty table. */
  error?: Error | null;
  emptyText?: string;
}

/** Generic server-driven table: pagination, sorting and filtering are all
 *  delegated to the caller's query params — this component never slices
 *  or reorders `data` itself. */
export function DataTable<T>({
  columns,
  data,
  total,
  page,
  limit,
  sort,
  onPageChange,
  onSortChange,
  isLoading,
  error,
  emptyText = 'Không có dữ liệu.',
}: DataTableProps<T>) {
  const table = useReactTable({
    data,
    columns,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    rowCount: total,
    getCoreRowModel: getCoreRowModel(),
  });

  const [sortField, sortDir] = sort.split(':');
  const pageCount = Math.max(1, Math.ceil(total / limit));

  const toggleSort = (field: string) => {
    const nextDir = sortField === field && sortDir === 'asc' ? 'desc' : 'asc';
    onSortChange(`${field}:${nextDir}`);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-md border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta as DataTableColumnMeta | undefined;
                  const field = meta?.sortField;
                  const isActive = field !== undefined && field === sortField;

                  return (
                    <TableHead key={header.id} className={cn('h-9', meta?.headerClassName)}>
                      {header.isPlaceholder ? null : field ? (
                        <button
                          type="button"
                          onClick={() => toggleSort(field)}
                          className={cn(
                            'inline-flex items-center gap-1 hover:text-surface-900',
                            isActive && 'text-surface-900',
                          )}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {isActive &&
                            (sortDir === 'asc' ? (
                              <ChevronUp className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronDown className="h-3.5 w-3.5" />
                            ))}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  Đang tải...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-destructive">
                  Không thể tải dữ liệu. Vui lòng thử lại sau.
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  {emptyText}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta as DataTableColumnMeta | undefined;
                    return (
                      <TableCell key={cell.id} className={cn('py-2', meta?.cellClassName)}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span className="tabular-nums">
          Trang {page} / {pageCount} · {total} bản ghi
        </span>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            Trước
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page >= pageCount}
            onClick={() => onPageChange(page + 1)}
          >
            Sau
          </Button>
        </div>
      </div>
    </div>
  );
}
